'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { Search, Heart, MapPin, Filter, Sparkles, ArrowRight, Shield } from 'lucide-react';
import Link from 'next/link';
import AppLayout from '@/components/AppLayout';

interface DatingProfile {
  id: string;
  display_name: string;
  age: number;
  location: string;
  bio: string;
  gender: string;
  is_verified: boolean;
  photo_url: string;
  interests: string[];
}

const SAMPLE_PROFILES: DatingProfile[] = [
{ id: '1', display_name: 'Amina W', age: 29, location: 'Nairobi', bio: 'Love hiking and cooking. Looking for genuine connection.', gender: 'female', is_verified: true, photo_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1246d41fb-1764802362452.png", interests: ['Hiking', 'Cooking', 'Travel'] },
{ id: '2', display_name: 'John K', age: 32, location: 'Nairobi', bio: 'Software engineer, coffee lover, weekend traveller.', gender: 'male', is_verified: false, photo_url: "https://img.rocket.new/generatedImages/rocket_gen_img_19e9eeadb-1767966401857.png", interests: ['Tech', 'Coffee', 'Travel'] },
{ id: '3', display_name: 'Grace O', age: 27, location: 'Kisumu', bio: 'Teacher by day, foodie by night. Nairobi born.', gender: 'female', is_verified: true, photo_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1116a4ec5-1776348985536.png", interests: ['Food', 'Reading', 'Music'] },
{ id: '4', display_name: 'Samuel K', age: 30, location: 'Mombasa', bio: 'Entrepreneur. Fitness enthusiast. Dog lover.', gender: 'male', is_verified: true, photo_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1449dda3e-1784530799195.png", interests: ['Fitness', 'Business', 'Dogs'] },
{ id: '5', display_name: 'Faith N', age: 26, location: 'Nairobi', bio: 'Creative designer with a passion for art and culture.', gender: 'female', is_verified: false, photo_url: "https://img.rocket.new/generatedImages/rocket_gen_img_164b67290-1772141566715.png", interests: ['Art', 'Design', 'Culture'] },
{ id: '6', display_name: 'David O', age: 34, location: 'Nakuru', bio: 'Doctor. Love outdoor adventures and good conversations.', gender: 'male', is_verified: true, photo_url: "https://img.rocket.new/generatedImages/rocket_gen_img_15afcbfb3-1780560764076.png", interests: ['Medicine', 'Outdoors', 'Conversations'] },
{ id: '7', display_name: 'Mary W', age: 28, location: 'Nairobi', bio: 'Accountant who loves dancing and weekend getaways.', gender: 'female', is_verified: true, photo_url: "https://img.rocket.new/generatedImages/rocket_gen_img_184f0c073-1784530799188.png", interests: ['Dancing', 'Travel', 'Finance'] },
{ id: '8', display_name: 'Peter M', age: 31, location: 'Eldoret', bio: 'Athlete and coach. Looking for someone to share life with.', gender: 'male', is_verified: false, photo_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1b9e499e2-1772599466266.png", interests: ['Sports', 'Fitness', 'Coaching'] }];


const LOCATIONS = ['All Locations', 'Nairobi', 'Mombasa', 'Kisumu', 'Nakuru', 'Eldoret'];
const GENDERS = ['All', 'Female', 'Male'];
const AGE_RANGES = ['Any Age', '18–25', '26–30', '31–35', '36–45', '45+'];

export default function DatingLandingPage() {
  const [search, setSearch] = useState('');
  const [location, setLocation] = useState('All Locations');
  const [gender, setGender] = useState('All');
  const [ageRange, setAgeRange] = useState('Any Age');
  const [showFilters, setShowFilters] = useState(false);
  const [likedIds, setLikedIds] = useState<Set<string>>(new Set());

  const filtered = SAMPLE_PROFILES.filter((p) => {
    const matchSearch = !search || p.display_name.toLowerCase().includes(search.toLowerCase()) || p.bio.toLowerCase().includes(search.toLowerCase());
    const matchLoc = location === 'All Locations' || p.location === location;
    const matchGender = gender === 'All' || p.gender === gender.toLowerCase();
    let matchAge = true;
    if (ageRange !== 'Any Age') {
      const [min, max] = ageRange.replace('+', '–200').split('–').map(Number);
      matchAge = p.age >= min && p.age <= max;
    }
    return matchSearch && matchLoc && matchGender && matchAge;
  });

  const toggleLike = useCallback((id: string) => {
    setLikedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  return (
    <AppLayout>
      <div className="min-h-screen" style={{ background: '#070C1A' }}>
        {/* Hero */}
        <div className="relative overflow-hidden px-4 pt-8 pb-10"
        style={{ background: 'linear-gradient(135deg, #1a0a1e 0%, #0d0a2e 50%, #070C1A 100%)' }}>
          <div className="absolute inset-0 opacity-20"
          style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #FF4081 0%, transparent 50%), radial-gradient(circle at 80% 20%, #7C4DFF 0%, transparent 50%)' }} />
          <div className="relative max-w-2xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-600 mb-4"
            style={{ background: 'rgba(255,64,129,0.15)', color: '#FF4081', border: '1px solid rgba(255,64,129,0.3)' }}>
              <Sparkles size={12} /> {SAMPLE_PROFILES.length.toLocaleString()}+ Active Profiles
            </div>
            <h1 className="text-3xl font-800 text-white mb-3 leading-tight">
              Find Your <span style={{ color: '#FF4081' }}>Perfect Match</span> in Kenya
            </h1>
            <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.55)' }}>
              Browse verified profiles from Nairobi, Mombasa, Kisumu and beyond. Sign up free to connect.
            </p>
            {/* Search */}
            <div className="relative max-w-md mx-auto">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: 'rgba(255,255,255,0.4)' }} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or interests…"
                className="w-full pl-10 pr-4 py-3.5 rounded-2xl text-sm outline-none"
                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff' }} />
              
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="px-4 py-3 sticky top-0 z-20" style={{ background: 'rgba(7,12,26,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="max-w-4xl mx-auto flex items-center gap-2 overflow-x-auto pb-1">
            <button onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-600 flex-shrink-0"
            style={{ background: showFilters ? 'rgba(255,64,129,0.15)' : 'rgba(255,255,255,0.06)', color: showFilters ? '#FF4081' : 'rgba(255,255,255,0.7)', border: `1px solid ${showFilters ? 'rgba(255,64,129,0.3)' : 'rgba(255,255,255,0.1)'}` }}>
              <Filter size={12} /> Filters
            </button>
            {LOCATIONS.map((l) =>
            <button key={l} onClick={() => setLocation(l)}
            className="px-3 py-2 rounded-xl text-xs font-600 flex-shrink-0 transition-all"
            style={{ background: location === l ? 'rgba(255,64,129,0.15)' : 'rgba(255,255,255,0.04)', color: location === l ? '#FF4081' : 'rgba(255,255,255,0.55)', border: `1px solid ${location === l ? 'rgba(255,64,129,0.3)' : 'rgba(255,255,255,0.08)'}` }}>
                {l}
              </button>
            )}
          </div>
          {showFilters &&
          <div className="max-w-4xl mx-auto mt-3 flex flex-wrap gap-2">
              {GENDERS.map((g) =>
            <button key={g} onClick={() => setGender(g)}
            className="px-3 py-1.5 rounded-xl text-xs font-600 transition-all"
            style={{ background: gender === g ? 'rgba(255,64,129,0.15)' : 'rgba(255,255,255,0.04)', color: gender === g ? '#FF4081' : 'rgba(255,255,255,0.55)', border: `1px solid ${gender === g ? 'rgba(255,64,129,0.3)' : 'rgba(255,255,255,0.08)'}` }}>
                  {g}
                </button>
            )}
              {AGE_RANGES.map((a) =>
            <button key={a} onClick={() => setAgeRange(a)}
            className="px-3 py-1.5 rounded-xl text-xs font-600 transition-all"
            style={{ background: ageRange === a ? 'rgba(255,64,129,0.15)' : 'rgba(255,255,255,0.04)', color: ageRange === a ? '#FF4081' : 'rgba(255,255,255,0.55)', border: `1px solid ${ageRange === a ? 'rgba(255,64,129,0.3)' : 'rgba(255,255,255,0.08)'}` }}>
                  {a}
                </button>
            )}
            </div>
          }
        </div>

        {/* Results count */}
        <div className="max-w-4xl mx-auto px-4 pt-4 pb-2 flex items-center justify-between">
          <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
            Showing <span style={{ color: '#FF4081', fontWeight: 700 }}>{filtered.length}</span> profiles
          </span>
          <Link href="/login"
          className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-700"
          style={{ background: '#FF4081', color: '#fff' }}>
            Join Free <ArrowRight size={12} />
          </Link>
        </div>

        {/* Profile Grid */}
        <div className="max-w-4xl mx-auto px-4 pb-8">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {filtered.map((profile) =>
            <div key={profile.id} className="relative rounded-2xl overflow-hidden group cursor-pointer"
            style={{ border: '1px solid rgba(255,255,255,0.07)' }}>
                <div className="relative aspect-[3/4]">
                  <img src={profile.photo_url} alt={`${profile.display_name} profile photo`}
                className="w-full h-full object-cover" loading="lazy" />
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, transparent 50%)' }} />
                  {/* Like button */}
                  <button onClick={() => toggleLike(profile.id)}
                className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center transition-all"
                style={{ background: likedIds.has(profile.id) ? '#FF4081' : 'rgba(0,0,0,0.5)' }}>
                    <Heart size={14} fill={likedIds.has(profile.id) ? '#fff' : 'none'} style={{ color: '#fff' }} />
                  </button>
                  {profile.is_verified &&
                <div className="absolute top-2 left-2 w-6 h-6 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(0,200,83,0.9)' }}>
                      <Shield size={10} style={{ color: '#fff' }} />
                    </div>
                }
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <p className="text-white font-700 text-sm leading-tight">{profile.display_name}, {profile.age}</p>
                    <div className="flex items-center gap-1 mt-0.5">
                      <MapPin size={10} style={{ color: 'rgba(255,255,255,0.6)' }} />
                      <span className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>{profile.location}</span>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {profile.interests.slice(0, 2).map((i) =>
                    <span key={i} className="text-[9px] px-1.5 py-0.5 rounded-md font-600"
                    style={{ background: 'rgba(255,64,129,0.25)', color: '#FF9EC0' }}>{i}</span>
                    )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* CTA */}
          <div className="mt-8 p-6 rounded-3xl text-center"
          style={{ background: 'linear-gradient(135deg, rgba(255,64,129,0.12), rgba(124,77,255,0.12))', border: '1px solid rgba(255,64,129,0.2)' }}>
            <h3 className="text-lg font-800 text-white mb-2">See All {12450 .toLocaleString()} Profiles</h3>
            <p className="text-sm mb-4" style={{ color: 'rgba(255,255,255,0.5)' }}>Create a free account to view full profiles, send messages, and find your match.</p>
            <Link href="/login"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-700 text-sm"
            style={{ background: '#FF4081', color: '#fff' }}>
              <Heart size={16} /> Create Free Account
            </Link>
          </div>
        </div>
      </div>
    </AppLayout>);

}